import React, { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import FocusTrap from 'focus-trap-react';

import Icon, { COLORS, ICONS } from "../icon";

import './modal.scss';
import "./ev-modal.scss";
import ModalPortal from "./modal-portal";
import { useCallback } from "react";
import Switch from "../switch/switch";
import { FieldLabel } from "../field/field";
import { throttle } from "underscore";

interface IModalBodyWrapper {
	useAdditionalWrapper: boolean;
}

const ModalBodyWrapper: React.FC<React.PropsWithChildren<IModalBodyWrapper>> = ({ useAdditionalWrapper, children }) => {
	if (useAdditionalWrapper) {
		return (
			<div className="additional-modal-body-wrapper">
				{children}
			</div>
		);
	}
	return (
		<>
			{children}
		</>
	);
};

export type ModalProps = {
	open: boolean;
	children: any;
	title?: string;
	onRequestClose: () => void;
	header?: any;
	footer?: any;
	cancellable: boolean;
	closeable: boolean;
	size?: 'normal' | 'medium' | 'large' | 'small' | 'desktop' | 'tablet' | 'mobile';
	subtitle?: string;
	closeOnEscape?: boolean;
	className?: string;
	noPadding?: boolean;
	hideHeader?: boolean;
	trapFocus?: boolean;
	focusTrapOptions?: { [key: string]: boolean };
	showOverflow?: boolean;
	padTitle?: boolean;
	eventPreview?: boolean;
	scrollBodyToBottom?: boolean; // Added for create breakout rooms session modal
	topRightToggle?: boolean;	// Added for create breakout rooms session modal
	onToggleClicked?: (value: any, on: boolean) => void; // Added for create breakout rooms session modal
	toggleLabel?: string // Added for create breakout rooms session modal
	toggleValue?: boolean // Added for create breakout rooms session modal
	throttleTime?: number;
	onBodyScroll?: (...args: any[]) => void;
	noCloseOnEscape?: boolean;
	closeByX?: boolean
	useAdditionalWrapper?: boolean
	portalClassName?: string;
};

export default function ModalComponent(props: ModalProps): JSX.Element {
	const {
		open,
		onRequestClose,
		children,
		title = '',
		header,
		footer,
		cancellable,
		closeable,
		size = "normal",
		subtitle,
		className,
		noPadding = false,
		hideHeader = false,
		trapFocus = true,
		showOverflow = false,
		padTitle = true,
		focusTrapOptions = { allowOutsideClick: true },
		eventPreview,
		scrollBodyToBottom,
		topRightToggle,
		onToggleClicked,
		toggleValue = false,
		toggleLabel,
		throttleTime = 100,
		onBodyScroll,
		noCloseOnEscape = false,
		closeByX = false,
		useAdditionalWrapper = false,
		portalClassName = '',
	} = props;

	const [isOpen, setIsOpen] = useState(false);
	const [openClass, setOpenClass] = useState("closed");

	const modalBodyRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Added this for the Breakout Rooms modal. When adding new rooms we flip the "scrollBodyToBottom" prop which
		// fires this to auto scroll smoothly to the bottom of the rooms list to bring the new one into view
		if (scrollBodyToBottom !== undefined) {
			setTimeout(() => {
				modalBodyRef.current?.scrollBy({ top: modalBodyRef.current.scrollHeight, behavior: "smooth" });
			}, 100);
		}
	}, [scrollBodyToBottom]);

	useEffect(() => {
		const escape = (e: any) => {
			if (e.key === 'Escape') {
				onRequestClose();
			}
		};

		if (open) {
			setIsOpen(true);
			setTimeout(() => setOpenClass("open"));
			if (!noCloseOnEscape) {
				window.addEventListener('keyup', escape);
			}
		} else {
			setOpenClass("closed");
		}

		return () => {
			window.removeEventListener('keyup', escape);
			document.body.style.overflow = '';
		};
	}, [open, onRequestClose, noCloseOnEscape]);

	const transitionEnded = useCallback(() => {
		if (!open) {
			setIsOpen(false);
		} else {
			// prevent body scrolling when modal is open
			document.body.style.overflow = 'hidden';
		}

	}, [open]);

	const Trap = trapFocus ? FocusTrap : React.Fragment;

	const trapProps = trapFocus ? { focusTrapOptions } : {};

	const PrimaryTooltip = (): JSX.Element => <Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12}></Icon>;

	const scrollbarVisible = useMemo(() => {
		if (modalBodyRef?.current && modalBodyRef.current.scrollHeight > modalBodyRef.current.clientHeight) {
			return true;
		} else {
			return false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalBodyRef.current?.scrollHeight, modalBodyRef.current?.clientHeight]);

	const handleBodyScroll = throttle((event: any) => {
		onBodyScroll?.(event?.target);
	}, throttleTime);

	return isOpen ? (
		<ModalPortal className={portalClassName}>
			<Trap {...trapProps}>
				<div
					className={classNames(className, "modal-wrapper", openClass)}
					onTransitionEnd={transitionEnded}
					style={{ pointerEvents: 'auto' }}
				>
					<div
						onClick={cancellable ? onRequestClose : () => ({})}
						className="modal-background"
					></div>
					<div className={classNames("modal", size, { 'no-padding': noPadding }, eventPreview ? 'event-preview' : '')}>
						<ModalBodyWrapper useAdditionalWrapper={useAdditionalWrapper}>
							<>
								{hideHeader ? null : (
									<div className={classNames("modal-header", { 'pad-title': padTitle }, closeByX ? "close-by-x" : "")}>
										<div className="modal-title-container">
											<h4 className="modal-title">{title}</h4>
											{closeByX &&
												<div className="closeByXIcon" onClick={onRequestClose}>
													<Icon name={ICONS.CLOSE} size={16} color={COLORS.WHITE} />
												</div>}
											{topRightToggle && onToggleClicked &&
												<div className="platform">
													<div className="field-group switch">
														<label>{toggleLabel}</label>
														<Switch
															on={toggleValue}
															value={toggleValue}
															onClick={onToggleClicked}
														/>
														<div className="tooltip">
															<FieldLabel label={" "} required={true} tooltip={[PrimaryTooltip, 'Toggling on Zoom Powered Rooms creates a unique Zoom link for each of your breakout rooms. Your attendees will have their breakout experience using Zoom.']} />
														</div>
													</div>
												</div>
											}
										</div>
										{closeable && (
											<button
												className="no-style close"
												onClick={onRequestClose}
											>
												<Icon
													name={ICONS.CLOSE}
													size={16}
													color={COLORS.WHITE}
												/>
											</button>
										)}
										{subtitle && <p className="modal-subtitle">{subtitle}</p>}
										{header}
									</div>
								)}
								<div
									ref={modalBodyRef}
									className={`modal-body ${showOverflow ? 'show-overflow' : ''} ${scrollbarVisible && "scrollbar-visible"}`}
									onScroll={handleBodyScroll}
								>{children}</div>
								{footer && <div className="modal-footer">{footer}</div>}
							</>
						</ModalBodyWrapper>
					</div>
				</div>
			</Trap>
		</ModalPortal>
	) : (
		<></>
	);
}
